import logoNormal from 'assets/img/logo-normal.svg'
import CustomMarker from 'components/ui/Map/CustomMarker'
import { useModal } from 'contexts/ModalContext'
import { Field, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import {
  LuCircleHelp,
  LuEuro,
  LuPencil,
  LuRefreshCcw,
  LuTrash2
} from 'react-icons/lu'
import { MapContainer, Popup, TileLayer } from 'react-leaflet'
import * as Yup from 'yup'

export default function InjuredFormDetail({
  isNew,
  user,
  fields,
  incidentRef,
  mapRef,
  handleSubmit,
  handleDelete
}) {
  // Hook para modal
  const { modal, openModal } = useModal()

  // useState()
  const [showCoordinatesTooltip, setShowCoordinatesTooltip] = useState(false)

  // Esquema de validación del formulario (POST o PUT):
  const validationSchema = isNew
    ? Yup.object().shape({
        incident_number: Yup.number()
          .typeError('El campo debe ser un número válido')
          .integer('El campo debe ser un número')
          .positive('El campo debe ser un número positivo')
          .required('Campo obligatorio'),
        agency_reference: Yup.number()
          .typeError('El campo debe ser un número válido')
          .integer('El campo debe ser un número')
          .required('Campo obligatorio'),
        coordinates: Yup.string()
          .matches(
            /^[0-9]*\.[0-9]+, ?[0-9]*\.[0-9]+$/,
            'Ingrese coordenadas en formato correcto'
          )
          .nullable(),
        payments: Yup.string()
          .matches(
            /^[0-9]+(\.[0-9]{1,2})?$/,
            'Ingrese un número en formato válido (ej: 1234.56)'
          )
          .nullable()
      })
    : Yup.object().shape({
        agency_reference: Yup.number()
          .typeError('El campo debe ser un número válido')
          .integer('El campo debe ser un número')
          .required('Campo obligatorio'),
        coordinates: Yup.string()
          .matches(
            /^[0-9]*\.[0-9]+, ?[0-9]*\.[0-9]+$/,
            'Ingrese coordenadas en formato correcto'
          )
          .nullable(),
        payments: Yup.string()
          .matches(
            /^[0-9]+(\.[0-9]{1,2})?$/,
            'Ingrese un número en formato válido (ej: 1234.56)'
          )
          .nullable()
      })

  return (
    <>
      <Formik
        initialValues={{ ...fields }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          handleSubmit(values)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          submitForm
        }) => (
          <Form
            ref={incidentRef}
            className="flex flex-col w-full p-6 mx-auto mt-6 bg-white border border-gray-200 rounded-md dark:border-gray-700 dark:bg-gray-800 print:m-0 print:p-0 print:border-0 gap-y-4 print:gap-y-2"
          >
            <div className="hidden w-full print:flex">
              <div className="flex flex-row items-center justify-between w-full overflow-hidden border-white rounded-sm bg-base">
                <img className="-mt-4 -mb-5 size-28" src={logoNormal} alt="" />

                <h2 className="pr-3 text-lg font-medium text-white dark:text-white">
                  Siniestro TUS - Lesionados (
                  {fields?.id !== 0 ? '#' + fields?.incident_number : 'nou'})
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 print:mt-0.5 print:gap-2">
              {/* Nº de Siniestro */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Nº de Siniestro"
                  className={`${touched.incident_number && errors.incident_number ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="incident_number"
                >
                  Nº de Siniestro
                </label>

                <Field
                  id="incident_number"
                  name="incident_number"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.incident_number && errors.incident_number ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  value={values?.incident_number}
                  disabled={user.role_id !== 1 || !isNew}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.incident_number && errors.incident_number ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.incident_number || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Referencia agencia */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Referencia agencia"
                  className={`${touched.agency_reference && errors.agency_reference ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="agency_reference"
                >
                  Referencia agencia
                </label>

                <Field
                  id="agency_reference"
                  name="agency_reference"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.agency_reference && errors.agency_reference ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.agency_reference && errors.agency_reference ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.agency_reference || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Referencia TUS */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Referencia TUS"
                  className={`${touched.tus_reference && errors.tus_reference ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="tus_reference"
                >
                  Referencia TUS
                </label>

                <Field
                  id="tus_reference"
                  name="tus_reference"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.tus_reference && errors.tus_reference ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.tus_reference && errors.tus_reference ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.tus_reference || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Riesgo siniestro */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Riesgo siniestro"
                  className={`${touched.incident_risk && errors.incident_risk ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="incident_risk"
                >
                  Riesgo siniestro
                </label>

                <Field
                  id="incident_risk"
                  name="incident_risk"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.incident_risk && errors.incident_risk ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.incident_risk && errors.incident_risk ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.incident_risk || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* F. Ocurrencia */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="F. Ocurrencia"
                  className={`${touched.occurrence_date && errors.occurrence_date ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="occurrence_date"
                >
                  F. Ocurrencia
                </label>

                <Field
                  id="occurrence_date"
                  name="occurrence_date"
                  type="date"
                  className={`block w-full leading-none p-2.5 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.occurrence_date && errors.occurrence_date ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1.5 print:mt-1`}
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.occurrence_date && errors.occurrence_date ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.occurrence_date || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hora Ocurrencia */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Hora Ocurrencia"
                  className={`${touched.occurrence_time && errors.occurrence_time ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="occurrence_time"
                >
                  Hora Ocurrencia
                </label>

                <Field
                  id="occurrence_time"
                  name="occurrence_time"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.occurrence_time && errors.occurrence_time ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.occurrence_time && errors.occurrence_time ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.occurrence_time || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Coordenadas */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <div className="flex flex-row items-center space-x-4">
                  <label
                    title="Coordenadas"
                    className={`${touched.coordinates && errors.coordinates ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                    htmlFor="coordinates"
                  >
                    Coordenadas
                  </label>

                  {user.role_id === 1 ? (
                    <>
                      <LuRefreshCcw
                        title={'Restablece las coordenadas al valor original'}
                        className="text-gray-500 cursor-pointer dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 print:hidden"
                        onClick={() => {
                          setFieldValue(
                            'coordinates',
                            fields.coordinates ? fields.coordinates : ''
                          )
                        }}
                        size={20}
                      />

                      <div className="relative inline-block print:hidden">
                        <LuCircleHelp
                          className="text-gray-500 cursor-help dark:hover:text-blue-500 dark:text-gray-300 hover:text-blue-500"
                          size={20}
                          onMouseEnter={() => setShowCoordinatesTooltip(true)}
                          onMouseLeave={() => setShowCoordinatesTooltip(false)}
                        />

                        <p
                          className={`${showCoordinatesTooltip ? 'flex' : 'hidden'} break-words border border-blue-400 absolute w-[300px] sm:w-[500px] px-5 py-3 text-left text-blue-700 -translate-x-44 sm:-translate-x-1/4 xl:-translate-x-1/2 bg-blue-100 rounded-lg shadow-lg -bottom-64 sm:-bottom-56 -mb-8 sm:-mb-4 left-1/2 dark:shadow-none shadow-blue-300/40 dark:bg-gray-800 dark:text-white`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4 absolute rotate-45 translate-x-6 sm:-translate-x-1/2 -ml-1.5 sm:ml-0 left-1/2 sm:left-1/4 xl:left-1/2 top-0 -mt-2 transform bg-blue-100 text-blue-100 border-t border-l border-blue-400 p-0 dark:text-gray-800 fill-current"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
                          </svg>

                          <span className="flex flex-col space-y-4">
                            <span className="flex flex-col ">
                              <span>
                                Debe ingresar las coordenadas en uno de estos
                                formatos:
                              </span>

                              <span className="pl-1.5">
                                · número.número,número.número
                              </span>

                              <span className="pl-1.5">
                                · número.número, número.número
                              </span>
                            </span>

                            <span>
                              No debe usar letras, símbolos o espacios al inicio
                              o final.
                            </span>

                            <span className="flex flex-col">
                              <span>Ejemplos válidos:</span>
                              <span className="pl-1.5 font-semibold">
                                · 12.345,67.89
                              </span>
                              <span className="pl-1.5 font-semibold">
                                · 12.345, 67.89
                              </span>
                            </span>
                          </span>
                        </p>
                      </div>
                    </>
                  ) : null}
                </div>

                <Field
                  id="coordinates"
                  name="coordinates"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.coordinates && errors.coordinates ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.coordinates && errors.coordinates ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.coordinates || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Conductor */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Conductor"
                  className={`${touched.driver && errors.driver ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="driver"
                >
                  Conductor
                </label>

                <Field
                  id="driver"
                  name="driver"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.driver && errors.driver ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.driver && errors.driver ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.driver || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pagos */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Pagos"
                  className={`${touched.payments && errors.payments ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="payments"
                >
                  Pagos
                </label>

                <div className="flex flex-row items-center mt-2 space-x-1 print:mt-0">
                  <Field
                    id="payments"
                    name="payments"
                    type="text"
                    className={`block w-full p-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.payments && errors.payments ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                    autoComplete="new-password"
                    disabled={user.role_id !== 1}
                  />

                  <LuEuro
                    title={'Euro (€)'}
                    className={`${touched.payments && errors.payments ? 'text-red-700 dark:text-red-500' : 'text-gray-500 dark:text-gray-200'}`}
                    size={20}
                  />
                </div>

                <div
                  className={`flex justify-start items-start w-full ${touched.payments && errors.payments ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.payments || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Posición */}
              <div className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 print:col-span-6">
                <label
                  title="Estatus"
                  className={`${touched.status && errors.status ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="status"
                >
                  Estatus
                </label>

                <Field
                  id="status"
                  name="status"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.status && errors.status ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'} print:py-1 print:mt-1`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.status && errors.status ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.status || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div className="col-span-12 break-inside-auto">
                <label
                  title="Descripción"
                  className={`${touched.description && errors.description ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="description"
                >
                  Descripción
                </label>

                <Field
                  id="description"
                  name="description"
                  type="text"
                  as="textarea"
                  className={`h-auto min-h-11 max-h-52 block print:hidden w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.description && errors.description ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div className="hidden w-full px-2 py-1 mt-1 text-gray-700 whitespace-pre-wrap bg-white border border-gray-200 rounded-sm min-h-8 print:flex dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600">
                  {values?.description || '\u00A0'}
                </div>

                <div
                  className={`flex justify-start items-start w-full ${touched.description && errors.description ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.description || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div className="col-span-12 break-inside-auto">
                <label
                  title="Observaciones"
                  className={`${touched.observations && errors.observations ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="observations"
                >
                  Observaciones
                </label>

                <Field
                  id="observations"
                  name="observations"
                  type="text"
                  as="textarea"
                  className={`h-auto min-h-11 max-h-52 block print:hidden w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none dark:disabled:border-gray-700 dark:disabled:bg-gray-800/20 disabled:cursor-not-allowed ${touched.observations && errors.observations ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                  disabled={user.role_id !== 1}
                />

                <div className="hidden w-full px-2 py-1 mt-1 text-gray-700 whitespace-pre-wrap bg-white border border-gray-200 rounded-sm min-h-8 print:flex dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600">
                  {values?.observations || '\u00A0'}
                </div>

                <div
                  className={`flex justify-start items-start w-full ${touched.observations && errors.observations ? 'visible' : 'invisible'} print:hidden`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.observations || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mapa */}
            <div className="col-span-12 space-y-2 break-inside-avoid">
              <label
                title="Mapa"
                className="text-gray-700 dark:text-gray-200"
                htmlFor="incident_number"
              >
                Mapa
              </label>

              {(fields.coordinates === null ||
              fields.coordinates === undefined ||
              fields.coordinates === ''
                ? ''
                : fields.coordinates) !==
              (values.coordinates === null ||
              values.coordinates === undefined ||
              values.coordinates === ''
                ? ''
                : values.coordinates) ? (
                <div className={`flex justify-start items-start w-full`}>
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-amber-600 dark:text-amber-500">
                      Guarde el siniestro y vuelva a entrar para ver el mapa. El
                      mapa se regenerará automáticamente.
                    </p>
                  </div>
                </div>
              ) : fields.coordinates === null ||
                fields.coordinates === undefined ||
                fields.coordinates === '' ||
                !fields.coordinates.includes(',') ? (
                <div className={`flex justify-start items-start w-full`}>
                  <div className="flex w-full">
                    {user.role_id === 1 ? (
                      <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                        El mapa no se mostrará si el campo de coordenadas está
                        vacío. Por favor, complete este campo para visualizarlo.
                      </p>
                    ) : (
                      <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                        El mapa no se mostrará porque el campo de coordenadas
                        está vacío. Contacte con un administrador para completar
                        este campo y visualizarlo.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <MapContainer
                  id="map"
                  ref={mapRef}
                  className="w-full border border-gray-200 rounded-sm"
                  center={fields.coordinates.split(',')}
                  zoom={13}
                  minZoom={6}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" // Mapa default de leaflet
                    // url="https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png" // Mapa minimalista oscuro
                    // url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png" // Mapa minimalista claro
                    // url="https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.png" // Mapa satelite
                  />
                  <CustomMarker position={fields.coordinates.split(',')}>
                    <Popup>{fields.coordinates.split(',')}</Popup>
                  </CustomMarker>
                </MapContainer>
              )}
            </div>

            {user.role_id === 1 ? (
              <div className="flex justify-end pt-6 gap-x-4 print:hidden">
                {!isNew && (
                  <button
                    title="Eliminar"
                    type="button"
                    onClick={() =>
                      openModal('DeleteModal', {
                        itemName: fields.incident_number,
                        itemId: fields.id,
                        handleDelete
                      })
                    }
                    className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-red-500 border border-red-500 rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-red-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-red-500 focus:border-red-500 dark:focus:border-red-500"
                  >
                    <LuTrash2 size={20} />
                    <span>Eliminar</span>
                  </button>
                )}

                <button
                  title="Guardar"
                  type="submit"
                  className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 border rounded-sm bg-accent border-accent sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
                >
                  <LuPencil size={20} />
                  <span>Guardar</span>
                </button>
              </div>
            ) : null}
          </Form>
        )}
      </Formik>
    </>
  )
}

InjuredFormDetail.propTypes = {
  isNew: PropTypes.bool,
  user: PropTypes.object,
  fields: PropTypes.object,
  incidentRef: PropTypes.any,
  mapRef: PropTypes.any,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}
